"use client"

import { IconBrandWhatsapp, IconX } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import type { WhatsAppActiveCall } from "@/lib/whatsapp-calls-types"

interface WhatsAppResumeCallCardProps {
  call: WhatsAppActiveCall
  onResume: () => void
  onDismiss: () => void
  busy?: boolean
}

/**
 * "A call is still live" card, shown when GET /whatsapp-calls/active reports a
 * call this user is still on after their browser dropped it (refresh, crash,
 * closed lid). The sidecar holds a dropped call for 30 s, so this card is a
 * race against that grace window — resuming re-attaches the media socket in
 * `attach` mode rather than placing anything new.
 *
 * Amber, not red: nothing has failed yet. Occupies the same bottom-right slot
 * as the in-call bar, which never coexists with it (the card only shows while
 * the call state is idle).
 */
export function WhatsAppResumeCallCard({
  call,
  onResume,
  onDismiss,
  busy = false,
}: WhatsAppResumeCallCardProps) {
  const t = useTranslations("whatsappCalls")
  const who = call.lead?.full_name || call.from_number || call.to_number || null

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border border-amber-400 bg-amber-50 p-4 shadow-lg dark:border-amber-500/50 dark:bg-amber-950/60">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <IconBrandWhatsapp className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {who ? t("resume.titleWith", { name: who }) : t("resume.title")}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-amber-800 hover:text-amber-900 dark:text-amber-300"
          onClick={onDismiss}
          aria-label={t("resume.dismiss")}
        >
          <IconX className="h-3.5 w-3.5" />
        </Button>
      </div>

      <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
        {t("resume.hint")}
      </p>

      <Button
        className="mt-3 w-full bg-amber-600 text-white hover:bg-amber-700"
        size="sm"
        onClick={onResume}
        disabled={busy}
      >
        {busy ? t("resume.resuming") : t("resume.resume")}
      </Button>
    </div>
  )
}
